import React, { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, Trash2 } from 'lucide-react';
import api, { clientService, getStoredUser } from '../../api';
import { FolderTree } from '../portal/FolderTree';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

export const PortalUploadsPanel = ({ clientId }) => {
  const { toast } = useToast();
  const isOwner = getStoredUser()?.firmRole === 'owner';
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get(`/clients/${clientId}/folders`).then((r) => setData(r.data)).catch(() => setData({ years: [], archives: [], fyEndMonth: '', currentFy: { label: '', range: '' } })), [clientId]);
  useEffect(() => { load(); }, [load]);

  const download = async (a) => {
    setBusy(a.id);
    try {
      const r = await api.get(`/clients/${clientId}/archives/${a.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement('a');
      const name = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '')?.[1] || `${a.label} documents.zip`;
      el.href = url; el.download = name; el.click();
      URL.revokeObjectURL(url);
      load();
    } catch { toast({ title: 'Download failed', variant: 'destructive' }); } finally { setBusy(null); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete the ${a.label} archive and its ${a.fileCount} files from Accu Desk? Make sure you've saved the zip.`)) return;
    setBusy(a.id);
    try { await api.delete(`/clients/${clientId}/archives/${a.id}`); toast({ title: `${a.label} archive deleted` }); load(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Could not delete', variant: 'destructive' }); } finally { setBusy(null); }
  };
  const actions = (a) => (
    <span className="flex gap-1.5">
      <Button size="sm" variant="outline" className="h-8" disabled={busy === a.id} onClick={() => download(a)} data-testid={`archive-download-${a.fy}`}>
        {busy === a.id ? <Loader2 size={13} className="animate-spin mr-1"/> : <Download size={13} className="mr-1"/>}Download zip
      </Button>
      {isOwner && <Button size="sm" variant="outline" className="h-8 text-red-700" disabled={!a.downloadedAt || busy === a.id} title={a.downloadedAt ? 'Delete archive' : 'Download it first'} onClick={() => remove(a)} data-testid={`archive-delete-${a.fy}`}><Trash2 size={13}/></Button>}
    </span>
  );

  if (!data) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div>;
  return (
    <div className="space-y-3" data-testid="client-portal-uploads">
      <p className="text-xs text-stone-500">Documents the client sent through their portal, filed by financial year, document type and month. Each year is zipped automatically a month after it ends — download it, then the owner can delete it.</p>
      <FolderTree data={data} fileHref={(f) => clientService.fileUrl(f.fileId)} archiveActions={actions} emptyText="No portal uploads yet."/>
    </div>
  );
};
